import fieldsConfiguration from '@src/entities/ActionPath/includes/fields.json';
import { fieldValidator } from '@src/shared/includes/validation';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { NumberInput } from '@uhg-abyss/web/ui/NumberInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isEmpty, isNil, isUndefined, merge, orderBy } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

const {
  actionId: actionField,
  impactedSource: untrustedSourceField,
  retryAttempts: retryAttemptsField,
  retryInterval: retryIntervalField,
  retryIntervalUnit: retryIntervalUnitField,
} = fieldsConfiguration;

/**
 * Table
 *
 * @TODO - Needs description.
 *
 * @param actions
 * @param chronoUnits
 * @param dataKey
 * @param fields
 * @param form
 * @param handleReset
 * @param isLoading
 * @param remove
 * @param sources
 * @returns {Element}
 * @constructor
 */
export function Table({
  actions = {},
  chronoUnits = [],
  dataKey = '',
  fields = [],
  form = {
    formState: {
      errors: {},
    },
    getValues: () => {},
    resetField: () => {},
    setValue: () => {},
    validate: () => {},
  },
  handleReset = () => {},
  isActiveAndManual,
  isLoading = false,
  remove = () => {},
  sources = [],
}) {
  const assignments = form.getValues('remediation.assignments');
  /**
   * validateField
   *
   * @TODO - Needs description.
   *
   * @param index
   * @param field
   */
  const validateField = (index, field) => {
    form.validate(
      `remediation[assignments][${index}][${field}]`,
      () => {},
      () => {}
    );
  };

  /**
   * validateRetryInterval
   *
   * Validates the retryIntervalUnit and retryInterval fields.
   *
   * @param assignment
   * @param index
   */
  const validateRetryInterval = (assignment, index) => {
    if (assignment?.retryAttempts !== '0') {
      if (
        !isEmpty(assignment?.retryAttempts) &&
        isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.retryAttempts)
      ) {
        validateField(index, 'retryIntervalUnit');
        if (
          !isEmpty(assignment?.retryIntervalUnit) &&
          isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.retryIntervalUnit)
        ) {
          validateField(index, 'retryInterval');
        }
      }
    }
  };

  /**
   * Validate all fields when values change.
   */
  useEffect(() => {
    if (!isNil(assignments) && !isEmpty(assignments)) {
      assignments?.forEach((assignment, index) => {
        validateField(index, 'impactedSource');
        if (
          !isEmpty(assignment?.impactedSource) &&
          isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.impactedSource)
        ) {
          validateField(index, 'actionId');
          if (
            !isEmpty(assignment?.actionId) &&
            isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.actionId)
          ) {
            validateField(index, 'retryAttempts');
          }
          validateRetryInterval(assignment, index);
        }
      });
    }
  }, [assignments, form?.formState?.errors]);

  /**
   * untrustedSourceOptions
   *
   * used for untrusted source selectable options dropdown when adding actions
   */
  const untrustedSourceOptions = useMemo(() => {
    const options = [];

    if (!isEmpty(sources)) {
      sources.forEach((source) => {
        options.push({
          label: source?.codeDesc,
          value: source?.codeId,
        });
      });
    }

    orderBy(options, ['label'], ['asc']);

    options.unshift({
      label: 'Entity',
      value: 'ENTITY',
    });

    return options;
  }, [sources]);

  /**
   * renderCellUntrustedSource
   *
   * @TODO Needs description.
   */
  const renderCellUntrustedSource = (args) => {
    const { row } = args;
    const { index } = row;

    return (
      <SelectInput
        isDisabled={isActiveAndManual}
        {...untrustedSourceField}
        model={`remediation[assignments][${index}][impactedSource]`}
        onChange={(selectedSource) => {
          const selectedAction = form?.getValues(`remediation[assignments][${index}][actionId]`);

          if (!isNil(selectedAction) && !isEmpty(selectedAction)) {
            const theAction = actions?.content?.find((action) => {
              return action?.id === selectedAction && action?.allowableSystems?.includes(selectedSource);
            });

            if (isUndefined(theAction)) {
              form?.setValue(`remediation[assignments][${index}][actionId]`, '');
            }
          }

          validateField(index, 'impactedSource');
          validateField(index, 'actionId');
        }}
        options={untrustedSourceOptions}
        validators={{
          ...untrustedSourceField?.validators,
          ...{
            validate: {
              customValidator: (value) => {
                return fieldValidator(untrustedSourceField, value);
              },
            },
          },
        }}
      />
    );
  };

  /**
   * renderCellAction
   *
   * @TODO Needs description.
   */
  const renderCellAction = (args) => {
    const { row } = args;
    const { index } = row;

    return (
      <SelectInput
        {...actionField}
        isDisabled={
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.impactedSource) ||
          isEmpty(row?.original?.impactedSource) ||
          isActiveAndManual
        }
        model={`remediation[assignments][${index}][actionId]`}
        onChange={() => {
          validateField(index, 'impactedSource');
          validateField(index, 'actionId');
          validateField(index, 'retryAttempts');
        }}
        options={
          actions?.content
            ?.filter((action) => {
              const selectedSource = form?.getValues(`remediation[assignments][${index}][impactedSource]`);

              if (isEmpty(action?.allowableSystems)) {
                return true;
              }

              return action?.allowableSystems?.includes(selectedSource);
            })
            .map((action) => {
              return {
                label: action?.name,
                value: action?.id,
              };
            }) || []
        }
        validators={{
          ...actionField?.validators,
          ...{
            validate: {
              customValidator: (value) => {
                return fieldValidator(actionField, value);
              },
            },
          },
        }}
      />
    );
  };

  /**
   * renderCellRetryAttempts
   *
   * @TODO Needs description.
   */
  const renderCellRetryAttempts = (args) => {
    const { row } = args;
    const { index } = row;

    return (
      <NumberInput
        {...retryAttemptsField}
        isDisabled={
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.impactedSource) ||
          isEmpty(row?.original?.impactedSource) ||
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.actionId) ||
          isEmpty(row?.original?.actionId)
        }
        model={`remediation[assignments][${index}][retryAttempts]`}
        onChange={() => {
          if (assignments?.[index]?.retryAttempts === '0') {
            form?.resetField(`remediation[assignments][${index}][retryIntervalUnit]`);
            form?.resetField(`remediation[assignments][${index}][retryInterval]`);
          }

          validateField(index, 'impactedSource');
          validateField(index, 'actionId');
          validateField(index, 'retryAttempts');

          if (assignments?.[index]?.retryAttempts !== '0') {
            validateField(index, 'retryIntervalUnit');
          }
        }}
        validators={{
          ...retryAttemptsField?.validators,
          ...{
            validate: {
              customValidator: (value) => {
                return fieldValidator(retryAttemptsField, value);
              },
            },
          },
        }}
      />
    );
  };

  /**
   * renderCellRetryInterval
   *
   * @TODO Needs description.
   */
  const renderCellRetryInterval = (args) => {
    const { row } = args;
    const { index } = row;

    return (
      <TextInput
        {...retryIntervalField}
        className="remediation-assignments-retryInterval"
        id={`remediation-assignments-${index}-retryInterval`}
        isDisabled={
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.impactedSource) ||
          isEmpty(row?.original?.impactedSource) ||
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.actionId) ||
          isEmpty(row?.original?.actionId) ||
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.retryAttempts) ||
          isEmpty(row?.original?.retryAttempts) ||
          !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.retryIntervalUnit) ||
          isEmpty(row?.original?.retryIntervalUnit) ||
          row?.original?.retryAttempts === '0'
        }
        leftAddOn={
          <SelectInput
            {...retryIntervalUnitField}
            className="remediation-assignments-retryIntervalUnit"
            id={`remediation-assignments-${index}-retryIntervalUnit`}
            isDisabled={
              !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.impactedSource) ||
              isEmpty(row?.original?.impactedSource) ||
              !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.actionId) ||
              isEmpty(row?.original?.actionId) ||
              !isUndefined(form?.formState?.errors?.remediation?.assignments?.[index]?.retryAttempts) ||
              isEmpty(row?.original?.retryAttempts) ||
              row?.original?.retryAttempts === '0'
            }
            model={`remediation[assignments][${index}][retryIntervalUnit]`}
            onChange={() => {
              validateField(index, 'impactedSource');
              validateField(index, 'actionId');
              validateField(index, 'retryAttempts');

              if (assignments?.[index]?.retryAttempts !== '0') {
                validateField(index, 'retryIntervalUnit');
                validateField(index, 'retryInterval');
              }
            }}
            options={chronoUnits
              .map((chronoUnit) => {
                return {
                  label: chronoUnit?.codeDesc,
                  value: chronoUnit?.codeId,
                };
              })
              .filter((chronoUnit) => {
                return chronoUnit?.value !== 'SECONDS';
              })}
            validators={{
              ...retryIntervalUnitField?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(retryIntervalUnitField, value);
                  },
                },
              },
            }}
          />
        }
        model={`remediation[assignments][${index}][retryInterval]`}
        onChange={() => {
          validateField(index, 'impactedSource');
          validateField(index, 'actionId');
          validateField(index, 'retryAttempts');

          if (row?.original?.retryAttempts !== '0') {
            validateField(index, 'retryIntervalUnit');
            validateField(index, 'retryInterval');
          }
        }}
        validators={{
          ...retryIntervalField.validators,
          ...{
            validate: {
              customValidator: (value) => {
                return fieldValidator(retryIntervalField, value);
              },
            },
          },
        }}
      />
    );
  };

  /**
   * renderCellRetryActions
   *
   * @TODO Needs description.
   */
  const renderCellActions = (args) => {
    const { row, rows } = args;
    const { index } = row;

    return (
      <React.Fragment>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          id="removeField"
          isDisabled={isActiveAndManual}
          onClick={() => {
            if (rows.length === 1) {
              handleReset();
            } else {
              remove(index);
            }
          }}
          variant="custom"
        >
          <Flex alignItems="center">
            <div>
              <IconSymbol icon="cancel" variant="filled" />
              <IconSymbol icon="cancel" variant="outlined" />
            </div>
            <div>Remove</div>
          </Flex>
        </Link>
      </React.Fragment>
    );
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns.map((item) => {
      const column = item;

      if (column.Header === 'Untrusted Source') {
        column.Cell = renderCellUntrustedSource;
      }

      if (column.Header === 'Action') {
        column.Cell = renderCellAction;
      }

      if (column.Header === 'Retry Attempts') {
        column.Cell = renderCellRetryAttempts;
      }

      if (column.Header === 'Retry Interval') {
        column.Cell = renderCellRetryInterval;
      }

      if (column.accessor === 'actions') {
        column.Cell = renderCellActions;
      }

      return column;
    });
  }, []);

  /**
   * reset
   *
   * This function is intended to be passed to the common table component. It will reset the form state fields
   *
   * @param values
   */
  const reset = useCallback(
    (values) => {
      let theValues = values;

      if (isEmpty(values)) {
        theValues = assignments;
      }

      form?.resetField('remediation.assignments', { defaultValue: theValues });
      theValues.forEach((formFields, index) => {
        Object.keys(formFields).forEach((column) => {
          form.setValue(`remediation[assignments][${index}][${column}]`, formFields[column]);
        });
      });
    },
    [assignments]
  );

  return (
    <ErrorHandler location="src/features/ActionPaths/components/misc/Wizard/components/steps/RemediateRecords/components/Table/Table.jsx">
      {!isEmpty(fields) && !isEmpty(assignments) ? (
        <Styles>
          <TableComponent
            {...{
              columns,
              configuration: merge({}, configuration, {
                data: assignments,
                enableGroupBy: false,
                isLoading,
                reorderRows: !isActiveAndManual,
              }),
              dataKey,
              reset,
              rows: assignments,
            }}
          />
        </Styles>
      ) : (
        <React.Fragment />
      )}
    </ErrorHandler>
  );
}
